import _ from 'platform/utilities/data';
import constants from 'vets-json-schema/dist/constants.json';
import { SERVICE_CONNECTION_TYPES } from '../shared/constants';
import {
  disabilityActionTypes,
  MILITARY_CITIES,
  MILITARY_STATE_VALUES,
} from './constants';
import { viewifyFields } from './utils';
import { migrateBranches } from './utils/serviceBranches';
import { normalizeAddressLine } from '../shared/utils/contactInformationHelpers';

// ****************************************
// This entire file _may_ be obsolete once form 526EZ v1 is no longer supported
// Update: 7/2020 - v1 removed; but leaving this code in place a little longer
// since I don't know the impact of completely removing it will have; and
// leaving it in place doesn't hurt anything
// ****************************************
export const filterServiceConnected = (disabilities = []) =>
  disabilities.filter(
    d => d.decisionCode === SERVICE_CONNECTION_TYPES.serviceConnected,
  );

export const addNoneDisabilityActionType = (disabilities = []) =>
  disabilities.map(d =>
    _.set('disabilityActionType', disabilityActionTypes.NONE, d),
  );

export const setClaimTypeNewOnly = formData =>
  _.set(
    ['view:claimType'],
    {
      'view:claimingNew': true,
      'view:claimingIncrease': false,
    },
    formData,
  );

export default function prefillTransformer(pages, formData, metadata, state) {
  const prefillRatedDisabilities = data => {
    const { disabilities } = data;

    if (!disabilities) {
      return setClaimTypeNewOnly(data);
    }

    const transformedDisabilities = addNoneDisabilityActionType(
      filterServiceConnected(disabilities),
    );

    const newData = _.omit(['disabilities'], data);

    return transformedDisabilities.length
      ? _.set('ratedDisabilities', transformedDisabilities, newData)
      : setClaimTypeNewOnly(newData);
  };

  const prefillContactInformation = data => {
    const newData = _.omit(['veteran'], data);
    const { veteran } = data;

    if (veteran) {
      const { emailAddress, primaryPhone, mailingAddress } = veteran;
      newData.phoneAndEmail = {};
      if (emailAddress) {
        newData.phoneAndEmail.emailAddress = emailAddress;
      }
      if (primaryPhone) {
        newData.phoneAndEmail.primaryPhone = primaryPhone;
      }
      if (mailingAddress) {
        const isMilitaryCity = MILITARY_CITIES.includes(
          mailingAddress.city?.trim().toUpperCase(),
        );
        const isMilitaryState = MILITARY_STATE_VALUES.includes(
          mailingAddress.state?.trim().toUpperCase(),
        );
        const onMilitaryBase = isMilitaryCity || isMilitaryState;
        // map existing country name to country code
        const mappedCountryCode = constants.countries.find(
          c => c.label === mailingAddress.country,
        )?.value;
        mailingAddress.country = mappedCountryCode || mailingAddress.country;
        newData.mailingAddress = {
          // strip out any extra data. Maybe left over from v1?
          // see https://va.ghe.com/software/va.gov-team/issues/19423
          'view:livesOnMilitaryBase': onMilitaryBase,
          country: mailingAddress.country || '',
          // Normalize address fields at prefill time — trim leading/trailing
          // spaces and collapse consecutive spaces. This prevents the most
          // common source of invalid prefilled data (extra spaces from backend
          // systems). Note: save-in-progress can also produce un-normalized
          // data, which is handled by ReviewCardField's startInEdit option.
          addressLine1: normalizeAddressLine(mailingAddress.addressLine1) || '',
          addressLine2: normalizeAddressLine(mailingAddress.addressLine2),
          addressLine3: normalizeAddressLine(mailingAddress.addressLine3),
          city: normalizeAddressLine(mailingAddress.city) || '',
          state: mailingAddress.state || '',
          zipCode: mailingAddress.zipCode || '',
        };
      }
    }

    return newData;
  };

  const prefillServiceInformation = data => {
    const newData = _.omit(
      ['servicePeriods', 'reservesNationalGuardService'],
      data,
    );
    const { servicePeriods, reservesNationalGuardService } = data;
    if (servicePeriods || reservesNationalGuardService) {
      newData.serviceInformation = {};
      if (servicePeriods) {
        newData.serviceInformation.servicePeriods = servicePeriods;
      }
      if (reservesNationalGuardService) {
        newData.serviceInformation.reservesNationalGuardService =
          reservesNationalGuardService;
      }
    }
    // backend is prefilling with older branch names
    return migrateBranches(newData);
  };

  const prefillBankInformation = data => {
    const newData = _.omit(
      ['bankAccountType', 'bankAccountNumber', 'bankRoutingNumber', 'bankName'],
      data,
    );

    const { bankAccountType, bankAccountNumber, bankRoutingNumber, bankName } =
      data;

    if (bankAccountType && bankAccountNumber && bankRoutingNumber && bankName) {
      newData['view:originalBankAccount'] = viewifyFields({
        bankAccountType,
        bankAccountNumber,
        bankRoutingNumber,
        bankName,
      });

      // start the bank widget in 'review' mode
      newData['view:bankAccount'] = { 'view:hasPrefilledBank': true };
    }

    return newData;
  };

  const prefillStartedFormVersion = data => {
    const newData = _.omit(['startedFormVersion'], data);
    const { startedFormVersion } = data;

    if (startedFormVersion) {
      newData.startedFormVersion = startedFormVersion;
    }

    return newData;
  };

  const prefillSyncModern0781Flow = data => {
    const newData = _.omit(['syncModern0781Flow'], data);
    const { syncModern0781Flow } = data;

    if (syncModern0781Flow) {
      newData.syncModern0781Flow = syncModern0781Flow;
    }

    return newData;
  };

  const prefillDisabilityCompNewConditionsWorkflow = data => {
    const newData = _.omit(['disabilityCompNewConditionsWorkflow'], data);
    const { disabilityCompNewConditionsWorkflow } = data;

    if (disabilityCompNewConditionsWorkflow) {
      newData.disabilityCompNewConditionsWorkflow =
        disabilityCompNewConditionsWorkflow;
    }

    return newData;
  };

  const prefillDisabilityCompConditionsEvidenceMessagingTest = data => {
    const newData = _.omit(
      ['disabilityCompConditionsEvidenceMessagingTest'],
      data,
    );
    const { disabilityCompConditionsEvidenceMessagingTest } = data;

    if (disabilityCompConditionsEvidenceMessagingTest) {
      newData.disabilityCompConditionsEvidenceMessagingTest =
        disabilityCompConditionsEvidenceMessagingTest;
    }

    return newData;
  };

  const transformations = [
    prefillRatedDisabilities,
    prefillContactInformation,
    prefillServiceInformation,
    prefillBankInformation,
    prefillStartedFormVersion,
    prefillSyncModern0781Flow,
    prefillDisabilityCompNewConditionsWorkflow,
    prefillDisabilityCompConditionsEvidenceMessagingTest,
  ];

  const applyTransformations = (data = {}, transformer) =>
    transformer(data, state);

  return {
    metadata,
    formData: transformations.reduce(applyTransformations, formData),
    pages,
  };
}
